const MIN_GAMEPLAY_LEVEL = 1;
const MAX_GAMEPLAY_LEVEL = 50;

export interface RuntimeConfig {
  wsUrl: string;
  playerName: string;
  playerLevel: number | null;
  clientVersion: string;
  reconnectDelayMs: number;
  embedded: boolean;
}

interface RuntimeEnv {
  readonly VITE_REALTIME_WS_URL?: string;
  readonly REALTIME_PORT?: string;
  readonly VITE_GAME_PLAYER_NAME?: string;
  readonly VITE_GAME_PLAYER_LEVEL?: string;
  readonly VITE_GAME_CLIENT_VERSION?: string;
  readonly VITE_GAME_RECONNECT_MS?: string;
}

interface ResolveRuntimeConfigOptions {
  currentUrl?: string;
  env?: RuntimeEnv;
  search?: string;
}

export function readRuntimeConfigFromBrowser(): RuntimeConfig {
  return resolveRuntimeConfig({
    currentUrl: window.location.href,
    env: import.meta.env as RuntimeEnv,
    search: window.location.search,
  });
}

export function resolveRuntimeConfig(
  options: ResolveRuntimeConfigOptions = {},
): RuntimeConfig {
  const currentUrl = new URL(options.currentUrl ?? "http://localhost:5173/");
  const params = new URLSearchParams(options.search ?? currentUrl.search);
  const env = options.env ?? {};
  const realtimePort = parseInteger(
    params.get("realtimePort") ?? env.REALTIME_PORT,
    8787,
  );

  return {
    wsUrl:
      readNonEmpty(params.get("ws"), env.VITE_REALTIME_WS_URL) ??
      createDefaultWsUrl(currentUrl, realtimePort),
    playerName:
      readNonEmpty(params.get("name"), env.VITE_GAME_PLAYER_NAME) ?? "",
    playerLevel: parseOptionalLevel(
      params.get("level") ?? env.VITE_GAME_PLAYER_LEVEL,
    ),
    clientVersion:
      readNonEmpty(
        params.get("version"),
        env.VITE_GAME_CLIENT_VERSION,
        "game-runtime-mvp",
      ) ?? "game-runtime-mvp",
    reconnectDelayMs: parseInteger(
      params.get("reconnectMs") ?? env.VITE_GAME_RECONNECT_MS,
      1500,
    ),
    embedded: parseEmbeddedFlag(params.get("embedded")),
  };
}

export function createDefaultWsUrl(url: URL, port = 8787): string {
  const protocol = url.protocol === "https:" ? "wss:" : "ws:";
  const hostname = url.hostname || "127.0.0.1";

  if (isLocalDevHost(hostname)) {
    return `${protocol}//${hostname}:${port}`;
  }

  return `${protocol}//${url.host || hostname}/ws`;
}

function isLocalDevHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0"
  );
}

function readNonEmpty(
  ...values: Array<string | null | undefined>
): string | null {
  for (const value of values) {
    if (typeof value !== "string") {
      continue;
    }
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return null;
}

function parseInteger(
  value: string | null | undefined,
  fallback: number,
): number {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function parseOptionalLevel(value: string | null | undefined): number | null {
  if (!readNonEmpty(value)) {
    return null;
  }
  const parsed = parseInteger(value, 1);
  return Math.min(Math.max(parsed, MIN_GAMEPLAY_LEVEL), MAX_GAMEPLAY_LEVEL);
}

function parseEmbeddedFlag(value: string | null): boolean {
  return value === "1" || value === "true" || value === "hosted";
}
